import React, { useCallback, useEffect, useMemo, useState } from "react";
import config from "../config.js";
import { apiErrorMessage } from "../utils/apiError";

/**
 * EVERY position we hold at a vendor, across all materials.
 *
 * The per-material view answers "where is THIS steel", which only helps once you already
 * know which material to look at. The question an operator actually has is the other one —
 * what are vendors holding of ours — and there was no way to ask it.
 */
/* `onPick` receives the material as a {material_id, material_code, description} row rather
   than a bare id: the page's own selector stamps AI context from those fields BEFORE it
   fetches the detail, so an id alone lands undefined in that context. */
export default function AllVendorCustodyCard({ onPick }) {
  const API_BASE = config.API_BASE_URL;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openOnly, setOpenOnly] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/material_custody?open_only=${openOnly}`);
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setRows(await res.json());
    } catch (err) {
      setError(`Could not load vendor custody: ${err.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, openOnly]);

  useEffect(() => { load(); }, [load]);

  const qty = (v) => Number(v ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });
  const dt = (v) => (v ? new Date(v).toLocaleDateString() : "—");
  // ⚠ null is NOT zero. A position with no PO behind it has no basis for a plan, and showing
  // that as "0 expected back" would read as "the vendor keeps everything".
  const plan = (v) => (v == null ? <span className="text-muted">—</span> : qty(v));

  // THE REASON THIS ROLL-UP EARNS ITS PLACE. The per-material view answers "where is THIS
  // steel", which needs you to already suspect something. A spare stick nobody chased is
  // exactly what you only notice with the whole board in front of you.
  const owedBack = useMemo(
    () => rows.filter((r) => !r.closed_at)
              .reduce((s, r) => s + (r.expected_back == null ? 0 : Number(r.expected_back)), 0),
    [rows]);

  return (
    <div className="card border-warning">
      <div className="card-header bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
        <span><strong>Material at vendors</strong>{" "}
          <span className="text-muted small">— ours, but not on our floor</span>
          {owedBack > 0 && (
            <span className="badge bg-info text-dark ms-2">{qty(owedBack)} expected back</span>
          )}</span>
        <div>
          <div className="form-check form-check-inline">
            <input className="form-check-input" type="checkbox" id="custodyOpenOnly"
                   checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            <label className="form-check-label small" htmlFor="custodyOpenOnly">Open only</label>
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger py-2 small">{error}</div>}
        {loading && <div className="text-muted small">Loading…</div>}
        {!loading && !rows.length && (
          <div className="text-muted small mb-0">
            Nothing is at a vendor right now. Positions appear here when we issue our own
            material for a component a vendor makes for us.
          </div>
        )}
        {!!rows.length && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Material</th>
                  <th className="text-end">Still holding</th>
                  <th className="text-end">Should use</th>
                  <th className="text-end">Expected back</th>
                  <th className="text-end">Pieces made</th>
                  <th>Issued</th>
                  <th>PO</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.custody_id} className={r.closed_at ? "text-muted" : ""}>
                    <td><strong>{r.vendor_name || `Vendor #${r.vendor_id}`}</strong></td>
                    <td>
                      {onPick ? (
                        <button className="btn btn-link btn-sm p-0 text-start"
                                onClick={() => onPick({
                                  material_id: r.material_id,
                                  material_code: r.material_code,
                                  description: r.material_description,
                                })}>
                          {r.material_code || `#${r.material_id}`}
                        </button>
                      ) : (
                        <strong>{r.material_code || `#${r.material_id}`}</strong>
                      )}
                      <div className="text-muted small">{r.material_description}</div>
                    </td>
                    <td className="text-end">
                      {qty(r.quantity)}
                      <div className="text-muted small">of {qty(r.issued_quantity)}</div>
                    </td>
                    <td className="text-end">
                      {plan(r.expected_to_use)}
                      {r.still_to_use != null && Number(r.still_to_use) > 0 && (
                        <div className="text-muted small">{qty(r.still_to_use)} still to cut</div>
                      )}
                    </td>
                    <td className="text-end">
                      {r.expected_back != null && Number(r.expected_back) > 0 && !r.closed_at ? (
                        <span className="badge bg-info text-dark">{qty(r.expected_back)}</span>
                      ) : plan(r.expected_back)}
                      {Number(r.returned || 0) > 0 && (
                        <div className="small text-success">{qty(r.returned)} returned</div>
                      )}
                      {Number(r.written_off || 0) > 0 && (
                        <div className="small text-danger">{qty(r.written_off)} written off</div>
                      )}
                    </td>
                    <td className="text-end">
                      {Number(r.pieces_consumed || 0) > 0 ? qty(r.pieces_consumed) : "—"}
                    </td>
                    <td>{dt(r.opened_at)}
                      {r.closed_at && <div className="small">closed {dt(r.closed_at)}</div>}
                    </td>
                    <td className="small">{r.po_number || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
